import { mapState, mapGetters, mapActions } from "vuex"

import { STAGES as CHECKOUT_STAGES } from "@/lib/checkout-utils"
import isPreviewContext from "@/lib/is-platform/is-preview-context"
import { isBuilderContext } from "@/lib/utils"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { getPageAccess, findAccessRoot, REASONS } from "@/lib/access/utils"

const AUTH_MODAL_HASH = "#auth"

export default {
  data() {
    return {
      hasFetchedAccess: false
    }
  },

  computed: {
    ...mapState("pages", ["pageSubscriptions", "pageProgress"]),
    ...mapGetters("auth", [
      "hasCheckedAuthentication",
      "isAuthenticated",
      "currentUser"
    ]),
    ...mapGetters("access", ["isSubscriberOnly"]),
    ...mapGetters(["appAccess"]),

    isReady() {
      return this.hasCheckedAuthentication && this.hasFetchedAccess
    },

    isCheckingAccess() {
      return (
        !this.hasFetchedAccess ||
        (!this.hasCheckedAuthentication && this.isLocked)
      )
    },

    isAuthModalOpen() {
      return !!this.$route.hash?.startsWith(AUTH_MODAL_HASH)
    },

    showPageContent() {
      if (isPreviewContext() || isBuilderContext()) {
        return true
      }

      return !!this.pageAccess.granted
    },

    pageAccess() {
      if (isBuilderContext()) return { granted: true }

      return getPageAccess({
        ...this.pageAccessParams,
        currentUser: this.currentUser,
        appAccess: this.appAccess,
        pageSubscriptions: this.pageSubscriptions,
        pageProgresses: this.pageProgress
      })
    },

    isLocked() {
      return !this.pageAccess.granted
    },

    isLockedFromAuthentication() {
      return (
        this.isLocked &&
        this.pageAccess.reason === REASONS.REQUIRE_AUTHENTICATION
      )
    },

    isLockedFromPurchase() {
      return (
        this.isLocked && this.pageAccess.reason === REASONS.REQUIRE_PURCHASE
      )
    },

    isGlobalInheritance() {
      return !findAccessRoot({
        pageId: this.pageAccessParams.pageId,
        currentPageId: this.pageAccessParams.currentPageId
      })[0]
    }
  },

  mounted() {
    const { pageId, rootId } = this.pageAccessParams

    this.loadAccess({ pageId, rootId }).then(() => {
      this.hasFetchedAccess = true
    })
  },

  methods: {
    ...mapActions("pages", ["loadAccess"]),

    triggerPurchase() {
      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
        productIds: this.pageAccess.data.product_ids,
        overlay: this.pageAccess.data.purchase_overlay
      })
    },

    showAuthModal() {
      if (!this.isAuthModalOpen) this.$router.push(AUTH_MODAL_HASH)
    }
  },

  watch: {
    isReady: {
      immediate: true,
      handler: function() {
        if (this._inactive) return

        if (!this.isReady) return

        if (
          this.$route.hash &&
          this.$route.hash.startsWith(CHECKOUT_STAGES.START)
        )
          return

        if (this.isLockedFromPurchase) {
          const showAuthFirst =
            this.isGlobalInheritance &&
            this.appAccess.prompt_purchase_post_sign_up &&
            this.isSubscriberOnly

          if (EventBus.hasListener(EVENTS.POSTING_SIGN_UP_ACTION)) {
            EventBus.$once(EVENTS.FINISH_SIGN_UP_ACTION, this.triggerPurchase)
          } else if (showAuthFirst && !this.isAuthenticated) {
            this.showAuthModal()
          } else if (!this.isAuthModalOpen) {
            this.triggerPurchase()
          }
        } else if (this.isLockedFromAuthentication) {
          this.showAuthModal()
        }
      }
    },

    showPageContent() {
      if (this.showPageContent) {
        this.$nextTick(() => {
          window.scroll(0, 0)
        })
      }
    }
  }
}
